import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import { JsonView } from "react-json-view-lite";
import { jsonViewerStyles } from "../utils/jsonViewerStyles";
import { playJSONDownloadSound } from "../utils/soundUtils";
import { isRawFormType, RAW_FORM_TYPES } from "../utils/rawFormTypes";

// Shared dark editor styling (edit textarea).
const editorSx = {
  fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
  fontSize: "14px",
  "& .MuiInputBase-root": {
    fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
    fontSize: "14px",
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#3c3c3c" },
};

function safeParseForView(str) {
  try {
    return str ? JSON.parse(str) : {};
  } catch {
    return { error: "Stored JSON is not valid — enter Edit to fix." };
  }
}

/**
 * RawJsonEditor — the JSON-preview-only editor for non-PDF "mevi-sign" smart
 * forms. Shows two stacked panes — the form template and its companion
 * integrations.json entry — under a SINGLE Edit / Download / Apply control set.
 * Never runs the lossy parseFormJSON / generateFormJson rebuild.
 *
 * Apply validates the template (type in the raw enum, has id + version) and the
 * integration (a JSON object), then hands both back verbatim. Download triggers
 * two files (template + integration). App persists both under the rawFormJSON
 * blob and publishes them to form-templates.json + integrations.json.
 */
const RawJsonEditor = ({ templateJson = "", integrationJson = "", onApply, setSnackbar }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedTemplate, setEditedTemplate] = useState("");
  const [editedIntegration, setEditedIntegration] = useState("");
  const [templateError, setTemplateError] = useState(null);
  const [integrationError, setIntegrationError] = useState(null);

  const templateForView = safeParseForView(templateJson);
  const integrationForView = safeParseForView(integrationJson);
  const currentType = templateForView?.formJSON?.type || "";

  const handleEnterEditMode = () => {
    setEditedTemplate(templateJson || "");
    setEditedIntegration(integrationJson || "");
    setTemplateError(null);
    setIntegrationError(null);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedTemplate("");
    setEditedIntegration("");
    setTemplateError(null);
    setIntegrationError(null);
  };

  const handleApplyChanges = () => {
    // --- Template pane ---
    let template;
    try {
      template = JSON.parse(editedTemplate);
    } catch (error) {
      setTemplateError(`Invalid JSON: ${error.message}`);
      return;
    }
    if (!template || typeof template !== "object" || Array.isArray(template)) {
      setTemplateError("Form template must be a JSON object.");
      return;
    }
    if (!isRawFormType(template.formJSON?.type)) {
      setTemplateError(
        `formJSON.type must stay one of: ${RAW_FORM_TYPES.join(", ")} ` +
          `(got ${JSON.stringify(template.formJSON?.type)}).`,
      );
      return;
    }
    if (!template.id || typeof template.id !== "string") {
      setTemplateError("Form template needs a top-level string `id`.");
      return;
    }
    if (!template.version || typeof template.version !== "string") {
      setTemplateError("Form template needs a top-level string `version`.");
      return;
    }

    // --- Integration pane ---
    let integration;
    try {
      integration = JSON.parse(editedIntegration);
    } catch (error) {
      setIntegrationError(`Invalid JSON: ${error.message}`);
      return;
    }
    if (!integration || typeof integration !== "object" || Array.isArray(integration)) {
      setIntegrationError("Integration must be a JSON object.");
      return;
    }

    // Normalize whitespace only (key order + data preserved).
    if (onApply) {
      onApply(JSON.stringify(template, null, 2), JSON.stringify(integration, null, 2));
    }
    setIsEditMode(false);
    setTemplateError(null);
    setIntegrationError(null);
    if (setSnackbar) setSnackbar({ open: true, message: "Changes applied.", severity: "success" });
  };

  const downloadOne = async (str, suggestedName) => {
    const blob = new Blob([str || ""], { type: "application/json" });
    try {
      if ("showSaveFilePicker" in window) {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: [{ description: "JSON File", accept: { "application/json": [".json"] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      }
      throw new Error("File System Access API not supported");
    } catch (err) {
      if (err.name === "AbortError") return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = suggestedName;
      a.style.position = "fixed";
      a.style.left = "-9999px";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    }
  };

  // One Download click -> two files: template + integration.
  const handleDownload = async () => {
    const base = currentType || "smart-form";
    await downloadOne(templateJson, `${base}.json`);
    await downloadOne(integrationJson, `${base}-integration.json`);
    playJSONDownloadSound();
  };

  const renderPane = (title, viewData, editValue, onEditChange, error, clearError, minRows) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={clearError}>
          <strong>JSON Error:</strong> {error}
        </Alert>
      )}
      {isEditMode ? (
        <TextField
          multiline
          fullWidth
          value={editValue}
          onChange={(e) => onEditChange(e.target.value)}
          variant="outlined"
          minRows={minRows}
          maxRows={40}
          sx={editorSx}
        />
      ) : (
        <Box sx={{ bgcolor: "#1e1e1e", borderRadius: 1, p: 2, overflow: "auto", "& *": { fontWeight: "normal !important" } }}>
          <JsonView data={viewData} style={jsonViewerStyles} />
        </Box>
      )}
    </Box>
  );

  return (
    <Card>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, p: 2 }}>
        <Typography variant="h6">JSON Preview</Typography>
        <Chip
          label="HTML Smart Form"
          size="small"
          sx={{ bgcolor: "success.light", color: "common.black", fontWeight: 600 }}
        />
        {currentType && (
          <Typography variant="body2" color="text.secondary">
            {currentType}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
          {!isEditMode ? (
            <>
              <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEnterEditMode} size="small">
                Edit
              </Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload} size="small">
                Download
              </Button>
            </>
          ) : (
            <>
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelEdit} size="small" color="error">
                Cancel
              </Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleApplyChanges} size="small" color="success">
                Apply Changes
              </Button>
            </>
          )}
        </Box>
      </Box>
      <CardContent>
        {renderPane("Form Template", templateForView, editedTemplate, setEditedTemplate, templateError, () => setTemplateError(null), 16)}
        <Divider sx={{ my: 2 }} />
        {renderPane("Integration", integrationForView, editedIntegration, setEditedIntegration, integrationError, () => setIntegrationError(null), 8)}
      </CardContent>
    </Card>
  );
};

export default RawJsonEditor;
